import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Mouse — a tiny round grey mouse, storybook style: one plump little body,
// a big head with giant dish ears, a pink nose with whiskers and a thin pink
// tail trailing behind. Faces +Z, sits on y = 0. About 0.25 m to the ear tips.
//
// Rig layout (flat — each named node moves about its own pivot):
//   - "body"  : the plump base. Breathes (squash/stretch about the ground).
//   - "head"  : the head + muzzle + eyes. Bobs in idle.
//   - "ear-l" / "ear-r" : the giant dish ears. Flick during the twitch.
//   - "nose"  : muzzle + nose ball + whiskers. The twitch's star part.
// The tiny feet and the tail are static (unnamed).

const FUR = "#9a9aa8";        // soft blue-grey coat
const FUR_DARK = "#7d7d8c";   // shaded back
const CREAM = "#f6efe3";      // belly + inner face
const PINK = "#efb0c0";       // inner ears, nose, feet, tail
const EYE_WHITE = "#f7f3e8";  // eye whites (never pure white)
const PUPIL = "#332b33";      // dark plum pupils (never pure black)
const WHISKER = "#d8d2c6";    // whiskers

// The pivots the animations move the named nodes about.
const BODY_PIVOT: [number, number, number] = [0, 0, 0];           // ground contact
const HEAD_PIVOT: [number, number, number] = [0, 0.12, 0.03];     // the neck
const NOSE_PIVOT: [number, number, number] = [0, 0.135, 0.10];    // muzzle root
const EAR_L_PIVOT: [number, number, number] = [-0.055, 0.21, 0.02];
const EAR_R_PIVOT: [number, number, number] = [0.055, 0.21, 0.02];

// One big friendly eye: white ball, dark pupil slightly forward, tiny highlight.
function eye(x: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.024, 10, 8), color: EYE_WHITE,
      position: [x, 0.175, 0.112], name: "head" },
    { geometry: new THREE.SphereGeometry(0.013, 8, 6), color: PUPIL,
      position: [x, 0.175, 0.130], name: "head" },
    { geometry: new THREE.SphereGeometry(0.005, 6, 5), color: CREAM,
      position: [x + 0.006, 0.182, 0.139], name: "head" },
  ];
}

// One giant dish ear: a flattened grey disc with a pink inner disc.
function dishEar(side: 1 | -1): Part[] {
  const name = side > 0 ? "ear-r" : "ear-l";
  const x = side * 0.068;
  return [
    { geometry: new THREE.SphereGeometry(0.055, 10, 8), color: FUR,
      position: [x, 0.245, 0.015], rotation: [-0.15, 0, -side * 0.18],
      scale: [1, 1, 0.3], name },
    { geometry: new THREE.SphereGeometry(0.038, 8, 6), color: PINK,
      position: [x, 0.245, 0.028], rotation: [-0.15, 0, -side * 0.18],
      scale: [1, 1, 0.25], name },
  ];
}

// One whisker: a hair-thin bar angled out from the muzzle.
function whisker(side: 1 | -1, lift: number): Part {
  return {
    geometry: new THREE.BoxGeometry(0.065, 0.0035, 0.0035), color: WHISKER,
    position: [side * 0.048, 0.128 + lift, 0.135],
    rotation: [0, -side * 0.55, side * lift * 6], name: "nose",
  };
}

export const mouse = defineModel({
  id: "mouse",
  batchId: "mess",
  name: "Mouse",
  tags: ["mess", "cute", "animated", "creature", "animal", "mouse", "rodent", "tiny", "mammal"],
  summary: "A tiny round grey mouse with giant dish ears, a pink nose and whiskers.",
  description:
    "A storybook mouse: a plump grey ball of a body with a cream belly, a big " +
    "head carrying two giant dish ears, bead-bright eyes, a pink nose with " +
    "whiskers, tiny pink feet and a thin tail trailing behind. Breathes at " +
    "rest (idle) and twitches its nose and flicks its ears (twitch).",
  author: "models",
  footprint: 0.18,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // ── body (named, breathes) ──────────────────────────────────────────
      { geometry: new THREE.SphereGeometry(0.095, 12, 10), color: FUR,
        position: [0, 0.09, -0.01], scale: [1.0, 0.95, 1.08], name: "body" },
      // A darker cap over the back.
      { geometry: new THREE.SphereGeometry(0.075, 10, 8), color: FUR_DARK,
        position: [0, 0.115, -0.055], scale: [1.05, 0.85, 0.85], name: "body" },
      // Cream belly patch.
      { geometry: new THREE.SphereGeometry(0.06, 10, 8), color: CREAM,
        position: [0, 0.075, 0.055], scale: [0.95, 0.95, 0.6], name: "body" },

      // ── head (named) ─────────────────────────────────────────────────────
      { geometry: new THREE.SphereGeometry(0.075, 12, 10), color: FUR,
        position: [0, 0.155, 0.055], name: "head" },
      // Cream lower face.
      { geometry: new THREE.SphereGeometry(0.045, 8, 8), color: CREAM,
        position: [0, 0.13, 0.09], scale: [1.1, 0.8, 0.75], name: "head" },
      ...eye(0.034), ...eye(-0.034),
      ...dishEar(1), ...dishEar(-1),

      // ── nose cluster (named, twitches) ───────────────────────────────────
      // The little muzzle cone pointing forward.
      { geometry: new THREE.ConeGeometry(0.032, 0.055, 8), color: FUR,
        position: [0, 0.135, 0.125], rotation: [Math.PI / 2, 0, 0], name: "nose" },
      // The pink nose ball on the tip.
      { geometry: new THREE.SphereGeometry(0.014, 8, 6), color: PINK,
        position: [0, 0.135, 0.157], name: "nose" },
      whisker(1, 0.008), whisker(1, -0.006),
      whisker(-1, 0.008), whisker(-1, -0.006),

      // ── feet + tail (static) ─────────────────────────────────────────────
      { geometry: new THREE.SphereGeometry(0.02, 8, 6), color: PINK,
        position: [-0.038, 0.016, 0.075], scale: [1, 0.7, 1.4] },
      { geometry: new THREE.SphereGeometry(0.02, 8, 6), color: PINK,
        position: [0.038, 0.016, 0.075], scale: [1, 0.7, 1.4] },
      // The thin tail: two overlapping segments curving off behind, rooted
      // inside the rump so the chain never breaks, plus a tip ball. The z
      // euler bends the second segment's plan angle (a y euler on a cylinder
      // axis is a no-op).
      { geometry: new THREE.CylinderGeometry(0.007, 0.009, 0.10, 6), color: PINK,
        position: [0, 0.035, -0.125], rotation: [1.4, 0, 0] },
      { geometry: new THREE.CylinderGeometry(0.005, 0.007, 0.085, 6), color: PINK,
        position: [0.02, 0.025, -0.21], rotation: [Math.PI / 2, 0, 0.5] },
      { geometry: new THREE.SphereGeometry(0.009, 6, 5), color: PINK,
        position: [0.042, 0.024, -0.25] },
    ];
  },
  animations: [
    // Idle — a quick little breath (small creatures breathe fast) with a soft
    // head bob and the ears riding along.
    {
      id: "idle",
      label: "Idle breathing",
      duration: 2.0,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          scale: [
            { t: 0, value: [1, 1, 1], ease: "sine" },
            { t: 1.0, value: [1.03, 1.05, 1.03], ease: "sine" },
            { t: 2.0, value: [1, 1, 1], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.0, value: [0, 0.006, 0], ease: "sine" },
            { t: 2.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "nose", pivot: NOSE_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.0, value: [0, 0.006, 0], ease: "sine" },
            { t: 2.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "ear-l", pivot: EAR_L_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.0, value: [0, 0.006, 0], ease: "sine" },
            { t: 2.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "ear-r", pivot: EAR_R_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.0, value: [0, 0.006, 0], ease: "sine" },
            { t: 2.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },

    // Twitch — the signature move: the nose scrunches and wiggles (scale pulse
    // + tiny roll) while the dish ears flick one after the other.
    {
      id: "twitch",
      label: "Nose twitch",
      duration: 1.4,
      loop: true,
      tracks: [
        {
          target: "nose", pivot: NOSE_PIVOT,
          scale: [
            { t: 0, value: 1, ease: "sine" },
            { t: 0.12, value: 1.22, ease: "sine" },
            { t: 0.24, value: 1, ease: "sine" },
            { t: 0.36, value: 1.18, ease: "sine" },
            { t: 0.5, value: 1, ease: "sine" },
            { t: 1.4, value: 1, ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.12, value: [0, 0, 0.10], ease: "sine" },
            { t: 0.24, value: [0, 0, -0.10], ease: "sine" },
            { t: 0.36, value: [0, 0, 0.08], ease: "sine" },
            { t: 0.5, value: [0, 0, 0], ease: "sine" },
            { t: 1.4, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "ear-l", pivot: EAR_L_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.55, value: [0, 0, 0], ease: "sine" },
            { t: 0.7, value: [-0.3, 0, -0.12], ease: "sine" },
            { t: 0.85, value: [0, 0, 0], ease: "sine" },
            { t: 1.4, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "ear-r", pivot: EAR_R_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.75, value: [0, 0, 0], ease: "sine" },
            { t: 0.9, value: [-0.3, 0, 0.12], ease: "sine" },
            { t: 1.05, value: [0, 0, 0], ease: "sine" },
            { t: 1.4, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.25, value: [-0.06, 0, 0], ease: "sine" },
            { t: 0.5, value: [0, 0, 0], ease: "sine" },
            { t: 1.4, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
